import { useRef, useState, useSyncExternalStore, type PointerEvent } from 'react'
import { flushSync } from 'react-dom'
import type { ImagePlacement } from '../../lib/types'
import { isWrap, nearestSlot, pastSlop, placementAt, type Slot } from '../../lib/imageSize'
import { columnRows, layoutRect, playFlip, snapshotLayout } from './flip'

/** Where the hand has put the image so far in a drag. */
export interface HandTarget {
  placement: ImagePlacement
  /** The column block it sits just before (null = the end). */
  before: string | null
  /** Lines down that block (wraps beside a paragraph only). */
  offset: number
}

/** Presses on these do their own thing: the caption, the toolbar, the resize handle, the grip. */
const OWN_GESTURE = 'textarea, button, [role="slider"], [role="toolbar"], [draggable="true"]'

export const PLACEMENT_SAID: Record<ImagePlacement, string> = {
  'wrap-left': 'Wrapped left',
  'wrap-right': 'Wrapped right',
  inline: 'In the column',
  full: 'Full width',
}

// The image being dragged, shared by every ImageBlockView: a drag across a
// pin's range remounts the figure, and the new one must still look held.
let active: string | null = null
const listeners = new Set<() => void>()
const setActive = (id: string | null) => {
  active = id
  listeners.forEach((l) => l())
}
const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => listeners.delete(l)
}

const figureOf = (id: string) =>
  Array.from(document.querySelectorAll<HTMLElement>('figure[data-selectable]')).find((f) => f.getAttribute('data-selectable') === id) ?? null

/**
 * Every place the image could go, measured now: before each column block
 * outside Columns (a Columns group counts as one), and for a wrap, each
 * line down a paragraph too. Tops are where the image's top would be, so the
 * rows after a column image (which pushes them down) are measured without it.
 */
function measureSlots(fig: HTMLElement, wrap: boolean): { slots: Slot[]; current: Pick<Slot, 'before'> } {
  const own = fig.closest<HTMLElement>('[data-row]')
  const rows = columnRows(fig.closest('.wr-column') ?? document).filter((el) => el !== own)
  const idOf = (el: HTMLElement) => el.getAttribute('data-row') ?? el.getAttribute('data-cols')
  // Laid-out boxes, not painted ones: the last move may still be gliding into place (flip.ts).
  const ownTop = own ? layoutRect(own).top : 0
  // The rows after the image, and how far it pushes them down (nothing for a float).
  const after = own ? rows.findIndex((el) => own.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) : -1
  const space = after < 0 ? 0 : Math.max(0, layoutRect(rows[after]).top - ownTop)
  const slots: Slot[] = []
  rows.forEach((el, i) => {
    const r = layoutRect(el)
    const top = after >= 0 && i >= after ? r.top - space : r.top
    const before = idOf(el)
    slots.push({ before, offset: 0, top })
    if (!wrap) return
    // A wrap can start on any line of a paragraph (a heading or a card it can sit beside, but starts level with).
    const p = el.querySelector<HTMLElement>('.wr-t-body[contenteditable]')
    if (!p) return
    const lh = parseFloat(getComputedStyle(p).lineHeight)
    const pr = layoutRect(p)
    if (!(lh > 0)) return
    const lines = Math.round(pr.height / lh)
    for (let k = 1; k < lines; k++) slots.push({ before, offset: k, top: pr.top - (r.top - top) + k * lh })
  })
  const last = rows[rows.length - 1]
  if (last) {
    const r = layoutRect(last)
    slots.push({ before: null, offset: 0, top: (after >= 0 ? r.bottom - space : r.bottom) + 22 })
  }
  return { slots, current: { before: after >= 0 ? idOf(rows[after]) : null } }
}

/**
 * The image "hand" (Niki's #1 editor feature, Composer lab's wrap tool):
 * press any image and move it anywhere. The text reflows round it as
 * it goes — no guide, no zones drawn — so what you see is where it lands.
 * Across, the column's left third wraps it left, the right third right, the
 * middle puts it back in the column. Up and down, it moves between blocks,
 * and a wrap can start on any line of a paragraph, so the words above it run
 * full width and the ones beside it (and the paragraphs after) flow round it.
 *
 * Each step is applied live (`onMove`); Esc puts everything back (`onCancel`).
 * The drag listens on the window rather than capturing the pointer: moving
 * the image moves its node, which would drop a capture. Mouse and pen use the
 * photo itself; touch uses the hand handle, so a finger still scrolls.
 */
export function useImageHand({
  id,
  enabled,
  placement,
  offset,
  onStart,
  onMove,
  onCancel,
  onEnd,
}: {
  /** The image block's id (its figure is found by it, so a remount mid-drag is fine). */
  id: string
  /** Wide enough to wrap (md up), and not in a column cell: any image, selected or not, as in Composer lab. */
  enabled: boolean
  placement: ImagePlacement
  offset: number
  /** A drag began (so the composer can keep what to go back to). */
  onStart?: () => void
  onMove: (t: HandTarget) => void
  onCancel?: () => void
  /** The drag let go where it is (after its last `onMove`): one move, for undo. */
  onEnd?: () => void
}) {
  const props = useRef({ placement, offset, onStart, onMove, onCancel, onEnd })
  props.current = { placement, offset, onStart, onMove, onCancel, onEnd }
  const dragging = useSyncExternalStore(subscribe, () => active === id, () => false)
  const [said, setSaid] = useState('')
  // A drag outlives a remount of this figure (see `active`), so unmounting doesn't end it.
  const stop = useRef<(() => void) | null>(null)

  const start = (e: PointerEvent<HTMLElement>) => {
    const fig = figureOf(id)
    const row = fig?.closest('[data-row]')?.getBoundingClientRect()
    const box = fig?.getBoundingClientRect()
    if (!fig || !row || !box || row.width <= 0) return
    e.preventDefault() // no text selection, no native image drag
    e.stopPropagation()
    const pointer = e.pointerId
    const x0 = e.clientX
    const y0 = e.clientY
    const grab = y0 - box.top
    const column = { left: row.left, width: row.width }
    const from = { ...props.current }
    let target: HandTarget = { placement: from.placement, before: null, offset: from.offset }
    let moved = false
    let frame = 0
    let last = { x: x0, y: y0 }

    const step = () => {
      frame = 0
      const fig = figureOf(id)
      if (!fig) return
      const placement = placementAt(last.x, column, from.placement)
      const { slots, current } = measureSlots(fig, isWrap(placement))
      const here = { before: current.before, offset: isWrap(target.placement) ? target.offset : 0 }
      const slot = nearestSlot(slots, last.y - grab, here)
      if (!slot) return
      const next = { placement, before: slot.before, offset: slot.offset }
      if (next.placement === target.placement && next.before === here.before && next.offset === here.offset) return
      target = next
      // Rendered at once, then eased from where everything was painted (flip.ts).
      const before = snapshotLayout(id)
      flushSync(() => props.current.onMove(next))
      playFlip(before, id)
    }
    const move = (ev: globalThis.PointerEvent) => {
      if (ev.pointerId !== pointer) return
      last = { x: ev.clientX, y: ev.clientY }
      if (!moved) {
        if (!pastSlop(ev.clientX - x0, ev.clientY - y0)) return
        moved = true
        target = { ...target, before: measureSlots(fig, isWrap(from.placement)).current.before }
        props.current.onStart?.()
        setActive(id)
        document.documentElement.setAttribute('data-wr-hand', '')
      }
      ev.preventDefault()
      if (!frame) frame = requestAnimationFrame(step)
    }
    const finish = (commit: boolean) => {
      cleanup()
      if (!moved) return
      if (!commit) {
        const before = snapshotLayout(id)
        flushSync(() => props.current.onCancel?.())
        playFlip(before, id)
        return
      }
      if (frame) step()
      props.current.onEnd?.()
      if (target.placement !== from.placement) setSaid(PLACEMENT_SAID[target.placement])
      // The click that ends a drag isn't a click on whatever it ends over.
      const swallow = (c: MouseEvent) => {
        c.stopPropagation()
        c.preventDefault()
      }
      window.addEventListener('click', swallow, { capture: true, once: true })
      setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0)
    }
    const up = (ev: globalThis.PointerEvent) => {
      if (ev.pointerId === pointer) finish(true)
    }
    const cancel = (ev: globalThis.PointerEvent) => {
      if (ev.pointerId === pointer) finish(false)
    }
    const key = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape' || !moved) return
      ev.preventDefault()
      ev.stopPropagation()
      finish(false)
    }
    const cleanup = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', cancel)
      window.removeEventListener('keydown', key, true)
      if (frame) cancelAnimationFrame(frame)
      if (active === id) setActive(null)
      document.documentElement.removeAttribute('data-wr-hand')
      stop.current = null
    }
    stop.current?.()
    stop.current = cleanup
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', cancel)
    window.addEventListener('keydown', key, true)
  }

  /** On the photo: mouse and pen only, and not on the figure's own controls. */
  const onPointerDown = (e: PointerEvent<HTMLElement>) => {
    if (!enabled || e.pointerType === 'touch' || (e.pointerType === 'mouse' && e.button !== 0)) return
    if ((e.target as Element).closest?.(OWN_GESTURE)) return
    start(e)
  }
  /** On the hand handle: any pointer. */
  const onHandleDown = (e: PointerEvent<HTMLElement>) => {
    if (!enabled || (e.pointerType === 'mouse' && e.button !== 0)) return
    start(e)
  }

  return {
    /** Spread on the figure. */
    photo: { onPointerDown },
    /** Spread on the hand handle. */
    handle: { onPointerDown: onHandleDown },
    /** This image is being dragged now. */
    dragging,
    /** For an aria-live region: where the last drag put it. */
    said,
  }
}
